import { FundBasketSummary } from "creso-core/types";
import {
  FUND_BASKET_STRATEGY_OPTIONS,
  FUND_BASKET_TENURE_OPTIONS,
} from "creso-core/constants";
import { useMemo } from "react";
import { AmcLogo } from "@core/components/logo/amc-logo";
import { formatAsPercentage } from "creso-core/utils";
import { Button } from "web-design-system";
import { BarChart } from "lucide-react";
import { useNavigate, Link } from "@tanstack/react-router";
import { BasketActionAccordion } from "./basket-action-accordion";
import { AllocationPie } from "./allocation-pie";
import { BasketAllocationLineChart } from "./basket-allocation-line-chart";

export function BasketDetailsCard(props: { basket: FundBasketSummary }) {
  const { basket } = props;

  const navigate = useNavigate();

  const description = useMemo(() => {
    if (!basket) return null;
    const tenure = FUND_BASKET_TENURE_OPTIONS.find(
      (f) => f.value === basket.tenure
    )?.label;
    const strategy = FUND_BASKET_STRATEGY_OPTIONS.find(
      (f) => f.value === basket.strategy
    )?.label;
    return (
      <>
        {tenure} &bull; {strategy}
      </>
    );
  }, [basket]);

  if (!basket) return null;

  return (
    <>
      <div className="flex flex-row shrink-0">
        <span className="h-11" />
        <div className="flex min-w-0 flex-col gap-1">
          <Link
            to="/funds/basket/$basket_id"
            params={{ basket_id: basket.id }}
            className="text-base font-semibold"
          >
            {basket.name}
          </Link>
          <span className="text-xs text-muted-foreground">{description}</span>
        </div>
      </div>
      {basket.description && (
        <span className="text-xs text-foreground mt-6 line-clamp-3 shrink-0">
          {basket.description}
        </span>
      )}

      <div className="mt-6 shrink-0">
        <BasketAllocationLineChart basket={basket} />
      </div>

      <div className="rounded-lg bg-background border mt-6 lg:max-h-60 xl:max-h-75 overflow-auto border-border p-4 gap-5 flex flex-col">
        {basket.funds_summary.map((f) => (
          <div className="flex items-center gap-3" key={f.fund_scheme_id}>
            <AmcLogo
              size="sm"
              className="border border-border"
              amc_code={f.provider_company_id}
            />
            <Link
              to="/funds/$fund_id"
              params={{ fund_id: f.fund_id }}
              className="text-sm font-normal text-foreground min-w-0 line-clamp-2 mr-auto"
            >
              {f.scheme_name}
            </Link>
            <span className="ml-auto text-sm font-semibold">
              {formatAsPercentage(f.allocation_percentage)}
            </span>
            <AllocationPie percentage={Number(f.allocation_percentage)} />
          </div>
        ))}
      </div>

      <Button
        onClick={() =>
          navigate({
            to: "/funds/basket/$basket_id",
            params: { basket_id: basket.id },
          })
        }
        className="w-full mt-4 mb-8"
        variant="outline"
      >
        <BarChart />
        View analytics
      </Button>

      <div className="mt-auto">
        <BasketActionAccordion basketId={basket.id} />
      </div>
    </>
  );
}
